/* Shared domain contract; the sync script adapts only the backend module syntax. */
import registry from './profiles.json';
const fields = ['ProfileId', 'ProductionPurpose', 'LifePhase', 'ManagementRole',
  'ManagementStage', 'Sex', 'HousingSystem', 'MilkStatus', 'ReproductionStatus',
  'BirthDate', 'LastCalvingDate', 'Parity', 'DamAnimalUid', 'MastStartedAt', 'WeanedAt'];

function fail(code) { const error = new Error(code); error.code = code; throw error; }
function dateValid(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
}
function createProfile(id, overrides = {}) {
  const definition = registry.profiles[id];
  if (!definition) fail('cattle-invalid-profile');
  const profile = {ProfileId: id, ProductionPurpose: definition.ProductionPurpose,
    LifePhase: definition.LifePhase, ManagementRole: definition.ManagementRole,
    ManagementStage: definition.ManagementStage, Sex: 'unknown', HousingSystem: 'unknown',
    MilkStatus: 'unknown', ReproductionStatus: 'unknown', BirthDate: '', LastCalvingDate: '',
    Parity: null, DamAnimalUid: '', MastStartedAt: null, WeanedAt: null};
  for (const key of fields) if (overrides[key] !== undefined) profile[key] = overrides[key];
  return profile;
}
function validateProfile(profile) {
  const def = registry.profiles[profile.ProfileId];
  if (!def || profile.LifePhase !== def.LifePhase || profile.ManagementRole !== def.ManagementRole)
    fail('cattle-invalid-profile');
  if (!def.stages.includes(profile.ManagementStage) ||
      !registry.purposes.includes(profile.ProductionPurpose) ||
      (def.ProductionPurpose !== 'unknown' && profile.ProductionPurpose !== def.ProductionPurpose) ||
      !registry.sexes.includes(profile.Sex) || !registry.housing.includes(profile.HousingSystem) ||
      !registry.milkStatuses.includes(profile.MilkStatus) ||
      !registry.reproductionStatuses.includes(profile.ReproductionStatus)) fail('cattle-invalid-profile');
  if ((def.reproduction && profile.Sex !== 'female') ||
      (profile.ProfileId === 'breeding_bull' && profile.Sex !== 'male')) fail('cattle-invalid-sex');
  if ((profile.BirthDate && !dateValid(profile.BirthDate)) ||
      (profile.LastCalvingDate && !dateValid(profile.LastCalvingDate))) fail('cattle-invalid-date');
  if (profile.Parity !== null && (!Number.isInteger(profile.Parity) || profile.Parity < 0)) fail('cattle-invalid-parity');
  if (profile.LastCalvingDate && profile.BirthDate && profile.LastCalvingDate < profile.BirthDate) fail('cattle-invalid-date');
  if ((profile.MilkStatus === 'milking' || profile.MilkStatus === 'dry') && profile.ManagementRole !== 'dairy_cow') fail('cattle-invalid-profile');
  if (profile.MilkStatus === 'suckling' && profile.ManagementRole !== 'suckler_cow') fail('cattle-invalid-profile');
  if (profile.ReproductionStatus !== 'unknown' && profile.Sex !== 'female') fail('cattle-invalid-sex');
  if (typeof profile.DamAnimalUid !== 'string' || profile.DamAnimalUid.length > 128 || profile.DamAnimalUid.includes('/')) fail('cattle-invalid-profile');
  for (const key of ['MastStartedAt', 'WeanedAt']) if (profile[key] !== null && (!Number.isFinite(profile[key]) || profile[key] <= 0)) fail('cattle-invalid-date');
  return profile;
}
function switchProfile(profile, id) {
  const next = createProfile(id);
  for (const key of ['Sex', 'HousingSystem', 'BirthDate', 'LastCalvingDate', 'Parity', 'DamAnimalUid', 'WeanedAt', 'MastStartedAt', 'ReproductionStatus']) next[key] = profile[key];
  return next;
}
function applyEvent(previous, event) {
  if (!registry.events.includes(event.eventType) || !Number.isFinite(event.effectiveAt) || event.effectiveAt <= 0) fail('cattle-invalid-event');
  let next;
  if (event.eventType === 'profile_set') {
    next = createProfile(event.profile?.ProfileId, event.profile);
  } else {
    if (!previous) fail('cattle-profile-required');
    next = {...previous};
    switch (event.eventType) {
      case 'weaned':
        if (previous.LifePhase !== 'calf') fail('cattle-invalid-transition');
        next = switchProfile(previous, previous.ProductionPurpose === 'beef' ? 'beef_growing' : previous.ProductionPurpose === 'dairy' && previous.Sex === 'female' ? 'dairy_heifer' : 'youngstock');
        next.ProductionPurpose = previous.ProductionPurpose;
        next.WeanedAt = event.effectiveAt;
        break;
      case 'mast_started':
        if (previous.LifePhase === 'calf') fail('cattle-invalid-transition');
        next = switchProfile(previous, previous.LifePhase === 'adult' ? 'beef_finishing' : 'beef_growing');
        next.MastStartedAt = event.effectiveAt;
        next.ManagementStage = 'arrival';
        break;
      case 'calved':
        if (previous.Sex !== 'female' || !registry.profiles[previous.ProfileId].reproduction) fail('cattle-invalid-transition');
        next = switchProfile(previous, previous.ProductionPurpose === 'beef' ? 'suckler_cow' : 'dairy_cow');
        next.Parity = (previous.Parity || 0) + 1;
        next.LastCalvingDate = event.localDate || new Date(event.effectiveAt).toISOString().slice(0, 10);
        next.MilkStatus = next.ProfileId === 'suckler_cow' ? 'suckling' : 'milking';
        next.ReproductionStatus = 'open';
        break;
      case 'dry_off':
        if (previous.ManagementRole !== 'dairy_cow') fail('cattle-invalid-transition');
        next.MilkStatus = 'dry'; break;
      case 'pregnancy_confirmed':
      case 'breeding_started':
        if (!registry.profiles[previous.ProfileId].reproduction || previous.Sex !== 'female') fail('cattle-invalid-transition');
        next.ReproductionStatus = event.eventType === 'pregnancy_confirmed' ? 'pregnant' : 'breeding'; break;
      default: fail('cattle-invalid-event');
    }
  }
  const eventDate = event.localDate || new Date(event.effectiveAt).toISOString().slice(0, 10);
  if ((next.BirthDate && next.BirthDate > eventDate) || (next.LastCalvingDate && next.LastCalvingDate > eventDate)) fail('cattle-invalid-date');
  return validateProfile(next);
}
function projectEvents(events, at = Date.now()) {
  const replaced = new Set(events.filter(e => e.effectiveAt <= at && e.supersedesEventId).map(e => e.supersedesEventId));
  const ordered = events.filter(e => e.effectiveAt <= at && !replaced.has(e.eventId))
    .sort((a, b) => a.effectiveAt - b.effectiveAt || a.sequence - b.sequence);
  return ordered.reduce((profile, event) => applyEvent(profile, event), null);
}
function getProfileId(animal) {
  // Never infer a v2 profile from missing or unknown legacy production types.
  return animal?.SchemaVersion === 2 && registry.profiles[animal.ProfileId] ? animal.ProfileId : null;
}
function getCapabilities(animal) {
  const id = getProfileId(animal);
  const def = id ? registry.profiles[id] : null;
  return {profileId: id, reproduction: !!def?.reproduction && animal.Sex === 'female',
    clinicalCalfScores: !!def?.clinicalCalfScores && animal.ManagementStage === 'pre_weaning',
    growth: !!def, fattening: animal?.ManagementRole === 'fattening'};
}
export {registry, fields, createProfile, validateProfile, applyEvent, projectEvents, getProfileId, getCapabilities};
